import { useState, useRef, useEffect } from "react";
import type { ReactNode } from "react";
import type { KeyboardEvent } from "react";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/MagnifyingGlass";
import { XIcon } from "@phosphor-icons/react/X";
import { Icon } from "../Icons/Icon";
import { Tooltip } from "../Feedback/Tooltip";
import { TextInput } from "./TextInput";
import { iconActionClasses } from "./Internal/buttonStyles";
import { useControlPresentation } from "./Internal/useControlPresentation";
import { inputBaseStyle, underlineStyle } from "./Internal/textInputStyles";
import {
  inputWidthClasses as widthClasses,
  type InputWidth,
} from "./inputWidth";

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * "default": standard TextInput style for pages and tabs.
   * "dialog": ghost pill style for dialog headers.
   * Defaults to "default".
   */
  variant?: "default" | "dialog";
  /**
   * When true, renders as a collapsed icon button that expands on click.
   * Pressing Escape or clicking the clear button collapses and clears.
   * When false, the input is always visible.
   * Defaults to false.
   */
  collapsed?: boolean;
  /** Input width. Applies to all variants. Defaults to w-40 for collapsible mode. */
  width?: InputWidth;
  /**
   * Hover help for the box as a whole — what this particular search covers,
   * where the placeholder only has room for how to phrase it.
   */
  tooltip?: ReactNode | undefined;
  /** Forwarded to the underlying input for test selection. */
  "data-testid"?: string | undefined;
}

export function SearchInput({ tooltip, ...props }: SearchInputProps) {
  const control = <SearchInputControl_ {...props} />;
  if (tooltip === undefined) {
    return control;
  }
  // `stretch` so the trigger wrapper doesn't shrink-wrap a full-width field —
  // but only when the field really is full width. The wrapper's `w-full` is
  // 100% of the parent, so on a fixed-width box it claims a whole line of a
  // flex row and pushes every neighbour onto a row of its own.
  const fillsItsContainer =
    props.variant !== "dialog" &&
    props.collapsed !== true &&
    (props.width === undefined || props.width === "full");
  return (
    <Tooltip content={tooltip} stretch={fillsItsContainer}>
      {control}
    </Tooltip>
  );
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
function SearchInputControl_({
  value,
  onChange,
  placeholder = "Search…",
  variant = "default",
  collapsed = false,
  width,
  "data-testid": dataTestid,
}: Omit<SearchInputProps, "tooltip">) {
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isExpanded = !collapsed || expanded;
  // The collapsible "default" variant hand-rolls its input (it needs a ref +
  // grid-expand animation, so it can't delegate to TextInput). Mirror TextInput
  // by honouring the control presentation: underline in the spacious flows,
  // boxed otherwise — so it matches its always-expanded sibling.
  const underlined = useControlPresentation() === "underlined";

  useEffect(() => {
    if (!expanded) return;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [expanded]);

  function handleCollapse() {
    onChange("");
    setExpanded(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") handleCollapse();
  }

  const widthClass = width ? widthClasses[width] : "w-40";

  // Always-expanded default: delegate to TextInput, with the same magnifying
  // glass the other three branches lead with — a search box should look like
  // one whether or not it happens to collapse.
  if (variant === "default" && !collapsed) {
    return (
      <TextInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        startIcon={MagnifyingGlassIcon}
        allowClear
        width={width}
        {...(dataTestid !== undefined && { "data-testid": dataTestid })}
      />
    );
  }

  // Always-expanded dialog: muted pill, always visible
  if (variant === "dialog" && !collapsed) {
    return (
      <div className="flex items-center rounded-md bg-muted">
        <span className="px-2 py-1 text-content-faint">
          <Icon icon={MagnifyingGlassIcon} size="sm" />
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onChange("");
          }}
          placeholder={placeholder}
          className={`${widthClass} bg-transparent py-1 text-sm text-foreground outline-none placeholder:text-content-faint`}
          {...(dataTestid !== undefined && { "data-testid": dataTestid })}
        />
        {value && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange("")}
            className="mr-2 ml-1 flex items-center text-content-faint"
            aria-label="Clear"
          >
            <XIcon size={12} />
          </button>
        )}
      </div>
    );
  }

  // Collapsible dialog: the collapsed trigger uses the shared icon-action style
  // so it matches sibling header actions (Button's `iconAction` look)
  if (variant === "dialog") {
    return (
      <div
        className={`flex items-center rounded-md transition-colors ${isExpanded ? "bg-muted" : ""}`}
      >
        {isExpanded ? (
          <span className="px-2 py-1 text-content-faint">
            <Icon icon={MagnifyingGlassIcon} size="sm" />
          </span>
        ) : (
          <Tooltip content="Search">
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setExpanded(true)}
              className={iconActionClasses(undefined, false, false)}
            >
              <Icon icon={MagnifyingGlassIcon} size="sm" weight="bold" />
            </button>
          </Tooltip>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isExpanded ? "1fr" : "0fr",
            transition: "grid-template-columns 200ms ease-in-out",
            overflow: "hidden",
          }}
        >
          <div style={{ overflow: "hidden", minWidth: 0 }}>
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              data-testid={dataTestid ?? "search-input"}
              className={`${widthClass} bg-transparent py-1 text-sm text-foreground outline-none placeholder:text-content-faint`}
            />
          </div>
        </div>

        {isExpanded && (
          <Tooltip content="Clear search">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleCollapse}
              className="mr-2 ml-1 flex items-center text-content-faint"
              aria-label="Clear search"
              data-testid="search-clear"
            >
              <XIcon size={12} />
            </button>
          </Tooltip>
        )}
      </div>
    );
  }

  // Collapsible default: standard TextInput style when expanded
  return (
    <div className="flex items-center">
      {isExpanded ? (
        <span className="px-2 py-1 text-content-faint">
          <Icon icon={MagnifyingGlassIcon} size="sm" />
        </span>
      ) : (
        <Tooltip content="Search">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center rounded-md px-2 py-1 text-content-faint transition-colors hover:bg-muted hover:text-content"
            aria-label="Search"
            data-testid="search-trigger"
          >
            <Icon icon={MagnifyingGlassIcon} size="sm" />
          </button>
        </Tooltip>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isExpanded ? "1fr" : "0fr",
          transition: "grid-template-columns 200ms ease-in-out",
          overflow: "hidden",
        }}
      >
        <div style={{ overflow: "hidden", minWidth: 0 }}>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            data-testid={dataTestid ?? "search-input"}
            className={
              underlined
                ? `${widthClass} ${inputBaseStyle(false, true)} px-0 pt-1 ${underlineStyle(false)}`
                : `${widthClass} rounded-md border-2 border-border bg-card px-3 py-1 text-sm font-medium text-foreground outline-none placeholder:text-content-faint focus:border-foreground/40`
            }
          />
        </div>
      </div>

      {isExpanded && (
        <Tooltip content="Clear search">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleCollapse}
            className="ml-1 flex items-center text-content-faint"
            aria-label="Clear search"
            data-testid="search-clear"
          >
            <XIcon size={12} />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
